import * as React from 'react';
import { useAuth } from '../state/auth-state';
import { useDomains } from '../state/domain-state';
import { useAuthorization } from '../hooks/use-authorization';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Spinner } from '../components/ui/spinner';
import { Alert, AlertDescription } from '../components/ui/alert';

/**
 * Admin Dashboard — landing page after login.
 * Shows summary of domains, subdomains, and recent activity.
 * Content is scoped by role: System_Admin sees all, Domain_Admin sees assigned only.
 *
 * Validates: Requirements 8.1, 8.2
 */
export function DashboardPage(): React.JSX.Element {
  const { state: authState } = useAuth();
  const { state: domainState, loadDomains } = useDomains();
  const { isSystemAdmin, role } = useAuthorization();

  React.useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  return (
    <section aria-labelledby="dashboard-heading">
      <div className="mb-6">
        <h2 id="dashboard-heading" className="text-2xl font-semibold text-foreground">
          Dashboard
        </h2>
        <p className="text-sm text-muted-foreground">
          Welcome back, {authState.user?.name ?? 'Admin'}. You are signed in as{' '}
          <span className="font-medium">{role}</span>.
        </p>
      </div>

      {domainState.error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{domainState.error}</AlertDescription>
        </Alert>
      )}

      {domainState.isLoading ? (
        <Spinner label="Loading dashboard..." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Domains</CardTitle>
              <CardDescription>
                {isSystemAdmin ? 'All registered domains' : 'Your assigned domains'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-primary">{domainState.domains.length}</p>
            </CardContent>
          </Card>

          {isSystemAdmin && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Role</CardTitle>
                <CardDescription>Your access level</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-lg font-semibold text-foreground">System Administrator</p>
                <p className="text-sm text-muted-foreground">
                  Full access to credentials, domains, and assignments.
                </p>
              </CardContent>
            </Card>
          )}

          {!isSystemAdmin && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Role</CardTitle>
                <CardDescription>Your access level</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-lg font-semibold text-foreground">Domain Administrator</p>
                <p className="text-sm text-muted-foreground">
                  Manage subdomains and assignments for your assigned domains.
                </p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
              <CardDescription>Common tasks</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <a
                href="/domains"
                className="block rounded-md px-3 py-2 text-sm text-primary hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                View domains →
              </a>
              <a
                href="/subdomains"
                className="block rounded-md px-3 py-2 text-sm text-primary hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                View subdomains →
              </a>
              {isSystemAdmin && (
                <a
                  href="/credentials"
                  className="block rounded-md px-3 py-2 text-sm text-primary hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  Manage credentials →
                </a>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </section>
  );
}
